import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { SkeletonLoader } from './BasicSkeletonShapes';
import { useSelector } from 'react-redux';
import { colorBackground, colorBorder } from '@/utils/palette';
import { Container } from 'react-bootstrap';

const MainWrapper = styled(Container)`
  display: flex;
  height: 100vh;
  max-height: 100vh;
  overflow: hidden;
  box-sizing: border-box;
  margin: 0 auto;
  max-width: ${({ screenWidth }) => (screenWidth > 2500 ? '2500px' : '100%')};
  width: 100%;
  * {
    box-sizing: border-box;
  }
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  box-sizing: border-box;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SidebarWrapper = styled.div`
  width: ${(props) => (props.collapse ? '50px' : '240px')};
  height: 100vh;
  background: ${colorBackground};
  padding: 20px;
  border-right: 1px solid ${colorBorder};
  transition: width 0.3s;
  overflow: hidden;
  box-sizing: border-box;
  flex-shrink: 0;
  position: sticky;
  top: 0;

  @media (max-width: 768px) {
    width: ${(props) => props.collapse && '50px'};
    height: auto;
    position: relative;
  }

  @media (max-width: 580px) {
    width: ${(props) => props.collapse && '0px'};
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
  opacity: ${(props) => (props.collapse ? 0 : 1)};
  transition: opacity 0.2s;
  overflow: hidden;
`;

const MainContent = styled.main`
  flex: 1;
  height: 100vh;
  padding: 24px;
  overflow-y: auto;
  overflow-x: hidden;
  box-sizing: border-box;
  @media (max-width: 768px) {
    padding: 16px;
    height: calc(100vh - 60px);
  }
`;

const Section = styled.div`
  margin-bottom: 32px;
  background: ${colorBackground};
  border: 1px solid ${colorBorder};
  border-radius: 8px;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${colorBorder};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: ${(props) => props.columns};
  gap: 16px;
  padding: 12px 16px;
  border-bottom: 1px solid ${colorBorder};
  align-items: center;
`;

const TableRow = styled(TableHeader)`
  border-bottom: 1px solid ${colorBorder};
  &:last-child {
    border-bottom: none;
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid ${colorBorder};
`;

export const VirtualMachinesSkeleton = ({ isLoading = false, children, detailMode = false }) => {
  const collapse = useSelector((state) => state?.sidebar?.collapse);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!isLoading) return children;

  const renderSidebarContent = () => (
    <SidebarContent collapse={collapse}>
      <SkeletonLoader
        shape='line'
        width='100%'
        height='36px'
        marginGap='0'
      />
      {Array.from({ length: 6 }).map((_, index) => (
        <SkeletonLoader
          key={index}
          shape='line'
          width='100%'
          height='24px'
          marginGap='0'
        />
      ))}
    </SidebarContent>
  );

  return (
    <MainWrapper screenWidth={screenWidth}>
      <ContentWrapper>
        {screenWidth < 2500 ? (
          <>
            {!collapse ? (
              <SidebarWrapper collapse={collapse}>
                <div
                  style={{
                    padding: window.innerWidth > 580 ? '0 16px' : '0',
                    marginBottom: window.innerWidth > 580 ? '32px' : '0',
                  }}
                ></div>
              </SidebarWrapper>
            ) : (
              <div style={{ width: '50px' }}></div>
            )}
          </>
        ) : (
          <div style={{ width: '100px' }}></div>
        )}

        <MainContent>
          <Section>
            <SectionHeader>
              <SkeletonLoader
                shape='line'
                width='120px'
                height='24px'
                marginGap='0'
              />
              <SkeletonLoader
                shape='line'
                width='100px'
                height='32px'
                marginGap='0'
              />
            </SectionHeader>
            <TableHeader columns='1fr 1fr 1fr'>
              <SkeletonLoader
                shape='line'
                width='100px'
                height='20px'
                marginGap='0'
              />
              <SkeletonLoader
                shape='line'
                width='100px'
                height='20px'
                marginGap='0'
              />
              <SkeletonLoader
                shape='line'
                width='100px'
                height='20px'
                marginGap='0'
              />
            </TableHeader>
            <TableRow columns='1fr 1fr 1fr'>
              <SkeletonLoader
                shape='line'
                width='50px'
                height='20px'
                marginGap='0'
              />
              <SkeletonLoader
                shape='line'
                width='50px'
                height='20px'
                marginGap='0'
              />
              <SkeletonLoader
                shape='line'
                width='80px'
                height='32px'
                marginGap='0'
              />
            </TableRow>
          </Section>

          <Section>
            <SectionHeader>
              <SkeletonLoader
                shape='line'
                width='120px'
                height='24px'
                marginGap='0'
              />
              <SkeletonLoader
                shape='line'
                width='100px'
                height='32px'
                marginGap='0'
              />
            </SectionHeader>
            <TableHeader columns='40px 1fr 1fr 1fr 1fr 80px'>
              {Array.from({ length: 6 }).map((_, index) => (
                <SkeletonLoader
                  key={index}
                  shape='line'
                  width='100%'
                  height='20px'
                  marginGap='0'
                />
              ))}
            </TableHeader>
            {Array.from({ length: 3 }).map((_, rowIndex) => (
              <TableRow
                key={rowIndex}
                columns='40px 1fr 1fr 1fr 1fr 80px'
              >
                {Array.from({ length: 6 }).map((_, colIndex) => (
                  <SkeletonLoader
                    key={colIndex}
                    shape='line'
                    width='100%'
                    height='20px'
                    marginGap='0'
                  />
                ))}
              </TableRow>
            ))}
            <PaginationWrapper>
              <SkeletonLoader
                shape='line'
                width='200px'
                height='24px'
                marginGap='0'
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                {Array.from({ length: 4 }).map((_, index) => (
                  <SkeletonLoader
                    key={index}
                    shape='line'
                    width='32px'
                    height='32px'
                    marginGap='0'
                  />
                ))}
              </div>
            </PaginationWrapper>
          </Section>

          <Section>
            <SectionHeader>
              <SkeletonLoader
                shape='line'
                width='120px'
                height='24px'
                marginGap='0'
              />
              <SkeletonLoader
                shape='line'
                width='100px'
                height='32px'
                marginGap='0'
              />
            </SectionHeader>
            <TableHeader columns='40px 1fr 1fr 1fr 1fr 1fr 1fr'>
              {Array.from({ length: 7 }).map((_, index) => (
                <SkeletonLoader
                  key={index}
                  shape='line'
                  width='100%'
                  height='20px'
                  marginGap='0'
                />
              ))}
            </TableHeader>
            {Array.from({ length: 3 }).map((_, rowIndex) => (
              <TableRow
                key={rowIndex}
                columns='40px 1fr 1fr 1fr 1fr 1fr 1fr'
              >
                {Array.from({ length: 7 }).map((_, colIndex) => (
                  <SkeletonLoader
                    key={colIndex}
                    shape='line'
                    width='100%'
                    height='20px'
                    marginGap='0'
                  />
                ))}
              </TableRow>
            ))}
            <PaginationWrapper>
              <SkeletonLoader
                shape='line'
                width='200px'
                height='24px'
                marginGap='0'
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                {Array.from({ length: 4 }).map((_, index) => (
                  <SkeletonLoader
                    key={index}
                    shape='line'
                    width='32px'
                    height='32px'
                    marginGap='0'
                  />
                ))}
              </div>
            </PaginationWrapper>
          </Section>
        </MainContent>
      </ContentWrapper>
    </MainWrapper>
  );
};
